<script setup>
import { useToggle, onKeyStroke } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { provideDropdownContext } from './provider.js';

const props = defineProps({
  fullWidth: {
    type: Boolean,
    default: false,
  },
  // SiteUp: seletores que NÃO contam como "clique fora". Necessário quando o
  // conteúdo do dropdown é teleportado pro <body> (menus da sidebar): aí ele
  // deixa de ser descendente deste container, e sem isto um clique dentro do
  // próprio menu fecharia o menu.
  ignoreClickOutside: {
    type: Array,
    default: () => [],
  },
  // SiteUp E4.3: origem do scale-in. A maioria dos dropdowns abre pra
  // baixo (cresce a partir do topo); os que ficam no rodapé de alguma
  // coisa (ex.: menu de perfil no fim da sidebar) abrem pra cima e devem
  // crescer a partir de baixo, senão o scale-in visualmente "cai de cima".
  openUpward: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);
const [isOpen, toggle] = useToggle(false);

const closeMenu = () => {
  if (isOpen.value) {
    emit('close');
    toggle(false);
  }
};

// SiteUp: Escape fecha o dropdown — no DOCUMENTO, não no container: depois de
// clicar numa opção o foco pode estar fora do container e o @keydown local
// nunca dispara. closeMenu é no-op quando já fechado.
onKeyStroke('Escape', () => closeMenu());

provideDropdownContext({
  isOpen,
  toggle,
  closeMenu,
});
</script>

<template>
  <div
    v-on-click-outside="[closeMenu, { ignore: props.ignoreClickOutside }]"
    class="relative space-y-2"
  >
    <slot name="trigger" :is-open :toggle="() => toggle()" />
    <!-- SiteUp E4.3: scale-in com transform-origin — mesmos tokens de
         movimento do resto do sistema (entrada rápida, saída um pouco mais
         curta). Dropdowns teleportados (menus da sidebar) NÃO passam por
         aqui — animam localmente, ver useDropdownContext() em cada um. -->
    <Transition
      enter-active-class="transition duration-[var(--motion-duration-enter)] ease-[var(--motion-ease-out)]"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-[var(--motion-duration-press)] ease-[var(--motion-ease-out)]"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute"
        :class="[
          props.openUpward ? 'origin-bottom' : 'origin-top',
          { 'inset-x-0 min-w-0 w-full': fullWidth },
        ]"
      >
        <!-- SiteUp: isOpen/closeMenu também no slot default — menus
             teleportados (sidebar) animam localmente e NÃO podem usar
             useDropdownContext() no componente pai do container: inject só
             desce de pai pra filho, e lá o provider explode no setup. -->
        <slot :is-open="isOpen" :close-menu="closeMenu" />
      </div>
    </Transition>
  </div>
</template>
